import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { MenuInfo } from 'rc-menu/es/interface';
import { useTranslation } from 'react-i18next';
import type { MenuProps } from 'antd';
import { UserOutlined } from '@ant-design/icons';

import { PageLayout, SideBarProps, TopBarProps, useThemeMenuItem } from '@ohpen/enso-ui';
import { getCdnAssets, useConfig, type CdnAssets, type IApp } from '@ohpen/react-utils';
import { useAuth } from '@ohpen/react-auth';
import { useAuthz } from '@ohpen/react-authz';

import { AppMenuItem, AppLayoutProps } from './interfaces';
import { getSideBarItems } from './sideBarItems';
import { ConfigLink } from './ConfigLink';
import { useHostChrome } from '@pmp/contexts';
import { useConfigAccess, useFeatureToggle } from '@pmp/hooks';

export const AppLayout = ({ children }: AppLayoutProps): ReactElement => {
  const config = useConfig<IApp>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const auth = useAuth();
  const { userPermissions, envFeatureFlags } = useAuthz();
  const enableDarkMode = useFeatureToggle(['enableDarkMode']);
  const { landingPath: configLandingPath } = useConfigAccess();
  const {
    devClient: { selectedClient, clientSelect },
    aboutInfo,
    userAccount,
    portalName,
  } = useHostChrome();
  const [cdnAssets, setCdnAssets] = useState<CdnAssets | null>(null);

  useEffect(() => {
    getCdnAssets({ ...config, CLIENT: selectedClient }).then(assets => {
      if (assets) setCdnAssets(assets);
    });
  }, [config, selectedClient]);

  const onMenuItemClick = useCallback(
    (info: MenuInfo) => {
      navigate(`/${info.key}`);
    },
    [navigate],
  );

  const sideBarItems: AppMenuItem[] = useMemo(
    () => getSideBarItems(userPermissions, envFeatureFlags),
    [userPermissions, envFeatureFlags],
  );

  const selected = useMemo(() => {
    const key = location.pathname.split('/')[1];
    const item = sideBarItems?.find(item => item?.key === key);
    return item?.key;
  }, [location.pathname, sideBarItems]);

  const items: AppMenuItem[] = useMemo(
    () =>
      sideBarItems.map(item => (item && 'label' in item && typeof item.label === 'string' ? { ...item, label: t(item.label) } : item)) ??
      [],
    [sideBarItems, t],
  );

  const sideBar: SideBarProps = useMemo(
    () => ({
      items: items as any,
      selectedKeys: [String(selected)],
      onClick: onMenuItemClick,
      width: 220,
    }),
    [items, onMenuItemClick, selected],
  );

  const { userDisplayName: displayName } = auth;

  // Placed here rather than via enso's showDarkModeToggle, which always renders after
  // extraItems: "My account" has to stay below the appearance entry.
  const themeMenuItem = useThemeMenuItem(enableDarkMode);

  const userMenuExtraItems: MenuProps['items'] = useMemo(() => {
    const entries: MenuProps['items'] = [];
    if (enableDarkMode) {
      entries.push(themeMenuItem);
    }
    entries.push({
      key: 'my-account',
      icon: <UserOutlined />,
      label: t('global.labels.myAccount'),
      onClick: userAccount.open,
    });
    return entries;
  }, [enableDarkMode, themeMenuItem, t, userAccount.open]);

  const onClickLogout = useCallback(async () => {
    await auth.logout();
  }, [auth]);

  const configMenu = useMemo(() => (configLandingPath ? <ConfigLink sectionPath={configLandingPath} /> : undefined), [configLandingPath]);

  const topBar: TopBarProps = useMemo(
    () => ({
      logo: { src: cdnAssets?.logoHorizontalPath ?? '', href: '/' },
      appName: portalName,
      userMenu: {
        name: displayName,
        onClickLogout,
        aboutInfo,
        extraItems: userMenuExtraItems,
      },
      customMenu: clientSelect,
      configMenu,
    }),
    [configMenu, cdnAssets?.logoHorizontalPath, portalName, clientSelect, onClickLogout, displayName, aboutInfo, userMenuExtraItems],
  );

  return (
    <PageLayout sideBar={sideBar} topBar={topBar}>
      {userAccount.modal}
      {children}
    </PageLayout>
  );
};
